/**
 * contextToBridge — adapts the unified {@link IEngineContext} (core) to the legacy
 * Redprint {@link EngineBridge} (the 14-method `ExecutionContext` contract) that
 * `GraphExecutor` still consumes.
 *
 * This lets the frozen Redprint graph interpreter run behind the single new boundary
 * without rewriting every node. The context is entity-bound, so the `entityId` argument
 * the old bridge passes is ignored (the bound entity is authoritative for Phase 3; graphs
 * operate on their own entity).
 */

import type { IEngineContext } from "@engine/core";
import type { EngineBridge } from "@engine/redprint";

export function contextToBridge(ctx: IEngineContext): EngineBridge {
  return {
    // Time
    getTime: () => ctx.time.time,
    getDeltaTime: () => ctx.time.deltaTime,

    // Transform (entity-bound; entityId arg ignored)
    getPositionX: () => ctx.transform.getPosition().x,
    getPositionY: () => ctx.transform.getPosition().y,
    setPosition: (_entityId: number, x: number, y: number) => ctx.transform.setPosition(x, y),
    getRotation: () => ctx.transform.getRotation(),
    setRotation: (_entityId: number, angle: number) => ctx.transform.setRotation(angle),
    getScaleX: () => ctx.transform.getScale().x,
    getScaleY: () => ctx.transform.getScale().y,
    setScale: (_entityId: number, x: number, y: number) => ctx.transform.setScale(x, y),

    // Sprite
    setSpriteColor: (_entityId: number, color: number) => ctx.sprite.setColor(color),
    setSpriteAlpha: (_entityId: number, alpha: number) => ctx.sprite.setAlpha(alpha),

    // Input (key treated as an action name; raw-key is a scoped extension)
    isKeyDown: (key: string) => ctx.engine.input?.isInputPressed(key) ?? false,
    isKeyPressed: (key: string) => ctx.engine.input?.isInputDown(key) ?? false,

    // Debug
    log: (message: string) => ctx.log.log(message),

    // Generic dispatch (not bridged in Phase 3)
    call: (method: string) => {
      ctx.log.warn(`[contextToBridge] bridge.call('${method}') is not supported via IEngineContext`);
      return undefined;
    },
  };
}
